import React, { useState, useEffect } from 'react'
import {
  Box, Typography, TextField, Button, Card, CardContent,
  CircularProgress, Alert, Avatar, Chip
} from '@mui/material'
import { EmojiEvents, ArrowForward } from '@mui/icons-material'
import { useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { eventQuizAPI } from '../../services/eventQuizAPI'
import toast from 'react-hot-toast'

const MAUVE_BG = 'linear-gradient(135deg, #0d001a 0%, #1a0035 55%, #2a0050 100%)'
const ACCENT = '#c77dff'
const BORDER = 'rgba(157,78,221,0.35)'

export default function EventQuizJoin() {
  const { code } = useParams()
  const navigate = useNavigate()

  const [sessionInfo, setSessionInfo] = useState(null)
  const [loading, setLoading] = useState(true)
  const [joining, setJoining] = useState(false)
  const [error, setError] = useState('')
  const [nickname, setNickname] = useState('')
  const [manualCode, setManualCode] = useState(code || '')

  useEffect(() => {
    if (code) loadSession(code)
    else setLoading(false)
  }, [code])

  const loadSession = async (roomCode) => {
    setLoading(true)
    setError('')
    try {
      const res = await eventQuizAPI.getJoinInfo(roomCode.toUpperCase())
      setSessionInfo(res.data)
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || 'Code invalide ou compétition introuvable'
      setError(msg)
      setSessionInfo(null)
    } finally {
      setLoading(false)
    }
  }

  const handleCodeSearch = (e) => {
    e.preventDefault()
    if (manualCode.trim().length < 6) return toast.error('Code à 6 caractères requis')
    loadSession(manualCode.trim())
  }

  const handleJoin = async (e) => {
    e.preventDefault()
    if (!nickname.trim() || nickname.trim().length < 2) return toast.error('Pseudo requis (2 caractères min)')
    if (!sessionInfo) return

    setJoining(true)
    try {
      const res = await eventQuizAPI.join(sessionInfo.session.roomCode, { nickname: nickname.trim() })
      localStorage.setItem(`participant-${sessionInfo.session.sessionId}`, JSON.stringify({
        participantId: res.data.participant.participantId,
        nickname: res.data.participant.nickname,
        sessionId: sessionInfo.session.sessionId,
        roomCode: sessionInfo.session.roomCode
      }))
      navigate(`/event-quiz/play/${sessionInfo.session.sessionId}`, {
        state: {
          participant: {
            ...res.data.participant,
            sessionId: sessionInfo.session.sessionId,
            roomCode: sessionInfo.session.roomCode
          }
        }
      })
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur lors de la connexion')
    } finally {
      setJoining(false)
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: MAUVE_BG }}>
        <CircularProgress sx={{ color: ACCENT }} />
      </Box>
    )
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      background: MAUVE_BG,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      p: 2
    }}>
      {/* Particules décoratives */}
      {[...Array(6)].map((_, i) => (
        <motion.div
          key={i}
          style={{
            position: 'fixed',
            width: 4, height: 4, borderRadius: '50%',
            background: i % 2 === 0 ? '#9d4edd' : '#f72585',
            left: `${10 + i * 15}%`, top: `${15 + i * 12}%`,
            opacity: 0.35, pointerEvents: 'none'
          }}
          animate={{ y: [-8, 8, -8] }}
          transition={{ duration: 3 + i, repeat: Infinity, ease: 'easeInOut' }}
        />
      ))}

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{ width: '100%', maxWidth: 460 }}
      >
        {/* Logo / En-tête */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200, delay: 0.1 }}
          >
            <Avatar sx={{
              width: 80, height: 80, mx: 'auto', mb: 2,
              background: 'linear-gradient(135deg, #7b2fff, #f72585)',
              fontSize: '2.2rem',
              boxShadow: '0 0 40px rgba(123,47,255,0.5)'
            }}>
              🏆
            </Avatar>
          </motion.div>
          <Typography variant="h4" fontWeight="900" sx={{
            background: 'linear-gradient(135deg, #c77dff, #ffffff)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
          }}>
            Quiz Compétition
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(199,125,255,0.7)', mt: 0.5 }}>
            Entrez le code pour rejoindre la compétition
          </Typography>
        </Box>

        {/* Carte principale */}
        <Card sx={{
          borderRadius: 4,
          background: 'rgba(26,0,53,0.85)',
          border: `1px solid ${BORDER}`,
          backdropFilter: 'blur(20px)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5), 0 0 40px rgba(123,47,255,0.15)'
        }}>
          <CardContent sx={{ p: 4 }}>

            {/* Saisie du code */}
            {!sessionInfo && (
              <Box component="form" onSubmit={handleCodeSearch}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ color: ACCENT }}>
                  Code de la salle
                </Typography>
                <TextField
                  fullWidth
                  value={manualCode}
                  onChange={(e) => setManualCode(e.target.value.toUpperCase().slice(0, 6))}
                  placeholder="AB12CD"
                  inputProps={{
                    style: {
                      textAlign: 'center', letterSpacing: 10,
                      fontSize: '2rem', fontWeight: 'bold', fontFamily: 'monospace',
                      color: '#c77dff'
                    },
                    maxLength: 6
                  }}
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'rgba(157,78,221,0.08)',
                      '& fieldset': { borderColor: BORDER },
                      '&:hover fieldset': { borderColor: ACCENT },
                      '&.Mui-focused fieldset': { borderColor: ACCENT }
                    },
                    '& input::placeholder': { color: 'rgba(199,125,255,0.3)', letterSpacing: 6 }
                  }}
                />
                {error && (
                  <Alert severity="error" sx={{ mb: 2, bgcolor: 'rgba(239,83,80,0.1)', border: '1px solid rgba(239,83,80,0.3)', color: '#ff8a80' }}>
                    {error}
                  </Alert>
                )}
                <Button
                  fullWidth variant="contained" size="large" type="submit"
                  disabled={manualCode.length < 6}
                  sx={{
                    background: 'linear-gradient(90deg, #7b2fff, #9d4edd)',
                    py: 1.5, fontWeight: 'bold', fontSize: '1rem',
                    boxShadow: '0 4px 20px rgba(123,47,255,0.4)',
                    '&:hover': { background: 'linear-gradient(90deg, #6a1fe0, #8a3dc5)', transform: 'translateY(-1px)' },
                    '&:disabled': { background: 'rgba(157,78,221,0.2)', color: 'rgba(255,255,255,0.3)' }
                  }}
                >
                  Rejoindre
                </Button>
              </Box>
            )}

            {/* Session trouvée : saisie pseudo */}
            {sessionInfo && (
              <Box component="form" onSubmit={handleJoin}>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Chip
                    icon={<EmojiEvents sx={{ color: '#FFD700 !important' }} />}
                    label={sessionInfo.quiz?.title || 'Quiz Compétition'}
                    sx={{
                      background: 'linear-gradient(90deg, rgba(123,47,255,0.3), rgba(247,37,133,0.2))',
                      color: 'white', border: `1px solid ${BORDER}`,
                      fontSize: '0.95rem', py: 2.5, px: 1, fontWeight: 'bold'
                    }}
                  />
                  <Typography variant="body2" sx={{ color: 'rgba(199,125,255,0.6)', mt: 1 }}>
                    {sessionInfo.session?.participantCount || 0} joueur(s) connecté(s)
                  </Typography>
                </Box>

                <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ color: ACCENT }}>
                  Votre pseudo
                </Typography>
                <TextField
                  fullWidth
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value.slice(0, 30))}
                  placeholder="Entrez votre pseudo…"
                  autoFocus
                  inputProps={{ maxLength: 30 }}
                  sx={{
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'rgba(157,78,221,0.08)', color: 'white',
                      '& fieldset': { borderColor: BORDER },
                      '&:hover fieldset': { borderColor: ACCENT },
                      '&.Mui-focused fieldset': { borderColor: ACCENT }
                    },
                    '& input': { color: 'white' },
                    '& input::placeholder': { color: 'rgba(199,125,255,0.4)' }
                  }}
                  helperText={
                    <Typography variant="caption" sx={{ color: 'rgba(199,125,255,0.5)' }}>
                      {nickname.length}/30
                    </Typography>
                  }
                />

                <Button
                  fullWidth variant="contained" size="large" type="submit"
                  disabled={joining || nickname.trim().length < 2}
                  endIcon={joining ? <CircularProgress size={18} sx={{ color: 'white' }} /> : <ArrowForward />}
                  sx={{
                    background: 'linear-gradient(90deg, #7b2fff, #f72585)',
                    py: 1.5, fontWeight: 'bold', fontSize: '1rem',
                    boxShadow: '0 4px 25px rgba(123,47,255,0.5)',
                    '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 30px rgba(247,37,133,0.4)' },
                    transition: 'all 0.2s'
                  }}
                >
                  {joining ? 'Connexion…' : 'C\'est parti !'}
                </Button>

                <Button
                  fullWidth variant="text"
                  sx={{ mt: 1.5, color: 'rgba(199,125,255,0.6)', '&:hover': { color: ACCENT } }}
                  onClick={() => { setSessionInfo(null); setError('') }}
                >
                  Changer de code
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Signature */}
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography variant="caption" sx={{ color: 'rgba(199,125,255,0.5)' }}>
            Cette plateforme a été fièrement construite par{' '}
            <a href="https://www.nouratech.sn" target="_blank" rel="noopener noreferrer"
              style={{ color: '#c77dff', fontWeight: 'bold', textDecoration: 'none' }}>
              Noura Tech
            </a>
          </Typography>
        </Box>
      </motion.div>
    </Box>
  )
}
